<script lang="ts">
	import { browser } from '$app/environment';
	import { Cookie } from 'lucide-svelte';
	import { onMount } from 'svelte';

	type Props = {
		privacyUrl: string;
		show?: boolean;
	};

	let { privacyUrl, show = $bindable(false) }: Props = $props();

	let preferencesOpen = $state(false);
	let consent = $state({
		necessary: true, // Necessary cookies are always enabled
		analytics: false,
		ads: false
	});

	onMount(() => {
		if (!browser) return;
		loadConsent();
	});

	function loadConsent() {
		const cookieConsent = getCookie('cookie_consent');
		if (cookieConsent) {
			try {
				consent = JSON.parse(cookieConsent);
				updateConsent(consent);
			} catch (e) {
				console.error('Error parsing cookie consent:', e);
				show = true; // Show modal if cookie is invalid
			}
		} else {
			show = true; // Show modal if no cookie
		}
	}

	function getCookie(name: string) {
		if (!browser) return null;
		const value = `; ${document.cookie}`;
		const parts = value.split(`; ${name}=`);
		if (parts.length === 2) return parts.pop()?.split(';').shift();
		return null;
	}

	function setCookie(name: string, value: string, days: number) {
		if (!browser) return;
		const d = new Date();
		d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
		let expires = 'expires=' + d.toUTCString();
		document.cookie = `${name}=${value};${expires};path=/;SameSite=Lax;Secure`;
	}

	function updateConsent(currentConsent: { necessary: boolean; analytics: boolean; ads: boolean }) {
		if (!browser || typeof window.gtag === 'undefined') return;
		window.gtag('consent', 'update', {
			analytics_storage: currentConsent.analytics ? 'granted' : 'denied',
			ad_storage: currentConsent.ads ? 'granted' : 'denied',
			ad_user_data: currentConsent.ads ? 'granted' : 'denied',
			ad_personalization: currentConsent.ads ? 'granted' : 'denied'
		});
	}

	function handleAcceptAll() {
		consent = { necessary: true, analytics: true, ads: true };
		setConsentCookie();
		show = false;
		updateConsent(consent);
	}

	function handleRejectAll() {
		consent = { necessary: true, analytics: false, ads: false };
		setConsentCookie();
		show = false;
		updateConsent(consent);
	}

	function handleSavePreferences() {
		setConsentCookie();
		preferencesOpen = false;
		show = false;
		updateConsent(consent);
	}
	function setConsentCookie() {
		if (!consent.analytics || !consent.ads) {
			setCookie('cookie_consent', JSON.stringify(consent), 1); // Cookie for 1 day
		} else {
			setCookie('cookie_consent', JSON.stringify(consent), 365); // Cookie for 1 year
		}
	}

	function openPreferences() {
		preferencesOpen = true;
	}

	function closePreferences() {
		preferencesOpen = false;
	}
</script>

{#if show}
	<div
		id="cookie-consent-modal"
		class="fixed bottom-9 right-5 z-50"
		role="dialog"
		aria-modal="true"
	>
		<div class="flex items-end justify-start text-left">
			<!-- Modal panel -->
			<div
				class="transform overflow-hidden rounded-lg text-left shadow-2xl transition-all max-w-sm bg-white dark:bg-slate-700 max-h-[80vh] flex flex-col"
				role="document"
			>
				<div class="px-4 pb-4 pt-5 sm:p-6 sm:pb-4 overflow-y-auto relative">
					<div class=" text-center sm:ml-4 sm:mt-0 sm:text-left">
						<div class="flex items-center gap-2" id="modal-title">
							<Cookie size={24} class="text-yellow-700 dark:text-yellow-500" />
							<h3 class="text-lg font-medium leading-6" id="modal-title">
								Cookies Make Things Better!
							</h3>
						</div>
						<div class="mt-4">
							<p class="text-sm font-normal">
								We use cookies to make your experience smoother, more personalized, and more
								relevant. They help us show you tailored content and offers, enhance site
								functionality, and ensure everything runs securely. By accepting cookies, you'll
								enjoy the best experience possible! Learn more in our
								<a href={privacyUrl} class="!underline">Privacy Policy</a>
							</p>
						</div>

						{#if preferencesOpen}
							<div class="mt-4">
								<h4 class="text-md font-medium leading-6">Cookie Preferences</h4>
								<p class="text-sm">
									Manage your cookie preferences below. You can change these at any time.
								</p>

								<div class="mt-4">
									<label class="flex items-center justify-between">
										<span>Analytics Cookies</span>
										<div
											class="relative mr-2 inline-block w-10 select-none align-middle transition duration-200 ease-in"
										>
											<input
												type="checkbox"
												id="analytics-toggle"
												class="toggle-checkbox absolute block h-6 w-6 cursor-pointer appearance-none rounded-full border-4 bg-white"
												bind:checked={consent.analytics}
											/>
											<label
												class="toggle-label block h-6 cursor-pointer overflow-hidden rounded-full bg-gray-300"
												for="analytics-toggle"
											></label>
										</div>
									</label>
									<p class="mt-1 text-sm">
										These cookies (e.g., Google Analytics) help us understand how visitors use our
										website. They allow us to count visits and traffic sources, so we can measure
										and improve the performance of our site. All information these cookies collect
										is aggregated and therefore anonymous.
									</p>
								</div>

								<div class="mt-4">
									<label class="flex items-center justify-between">
										<span>Advertising Cookies</span>
										<div
											class="relative mr-2 inline-block w-10 select-none align-middle transition duration-200 ease-in"
										>
											<input
												type="checkbox"
												id="ads-toggle"
												class="toggle-checkbox absolute block h-6 w-6 cursor-pointer appearance-none rounded-full border-4 bg-white"
												bind:checked={consent.ads}
											/>
											<label
												class="toggle-label block h-6 cursor-pointer overflow-hidden rounded-full bg-gray-300"
												for="ads-toggle"
											></label>
										</div>
									</label>
									<p class="mt-1 text-sm">
										These cookies (e.g., Google Ads, Meta Pixel) are used to deliver advertisements
										more relevant to you and your interests. They are also used to limit the number
										of times you see an advertisement as well as help measure the effectiveness of
										the advertising campaigns. They are usually placed by advertising networks with
										the website operator's permission.
									</p>
								</div>
							</div>
						{/if}
					</div>
				</div>
				<div class="px-4 py-3 flex flex-col gap-2 text-sm">
					{#if preferencesOpen}
						<button
							type="button"
							class="bg-primary text-white dark:text-black"
							onclick={handleSavePreferences}
						>
							Save Preferences
						</button>
						<button type="button" class="bg-one" onclick={closePreferences}> Cancel </button>
					{:else}
						<button
							type="button"
							class="bg-primary text-white dark:text-black"
							onclick={handleAcceptAll}
						>
							Sure! Accept All
						</button>

						<button type="button" class="bg-one" onclick={openPreferences}> Let Me Choose </button>

						<button type="button" class="bg-one" onclick={handleRejectAll}>
							Just the Essentials
						</button>
					{/if}
				</div>
			</div>
		</div>
	</div>
{/if}

<style>
	#cookie-consent-modal button {
		width: 100%;
		padding: 0.5rem 1rem;
		border-radius: 0.375rem;
		transition:
			background-color 0.2s ease,
			color 0.2s ease;
		cursor: pointer;
	}

	#cookie-consent-modal button:hover {
		transform: scale(1.02);
	}

	#cookie-consent-modal button:focus {
		outline: none;
		box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.5);
	}

	#cookie-consent-modal button:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}
	/* Toggle Switch Styles */
	#cookie-consent-modal .toggle-checkbox:checked {
		right: 0;
		left: auto; /* Ensure it moves to the right when checked */
		border-color: #2ed368;
		transform: translateX(0); /* Reset any transform */
	}

	#cookie-consent-modal .toggle-checkbox:checked + .toggle-label {
		background-color: #46e57e;
	}

	#cookie-consent-modal .toggle-checkbox {
		position: absolute;
		left: 0;
		top: 0;
		height: 24px;
		width: 24px;
		z-index: 1;
		transition: all 0.3s;
	}

	#cookie-consent-modal .toggle-label {
		position: relative;
		transition: background-color 0.3s ease;
	}
</style>
